import { Divider, Layout, Row, Spin, Switch, Typography, message } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import LockIcon from "../../../assets/icons/lock-outlined.svg";
import AddMediaComponent from "../../../components/AddMediaComponent";
import ArrowLeftIconComponent from "../../../components/ArrowLeftIconComponent";
import SpinnerComponent from "../../../components/SpinnerComponent/SpinnerComponent";
import StickyButtonComponent from "../../../components/StickyButtonComponent";
import UserIdentificationPublicationModalComponent from "../../../components/UserIdentificationPublicationModalComponent";
import { IFeed } from "../../../helpers/types";
import { addFeed } from "../../../redux/feeds/feedsSlice";
import { AppDispatch } from "../../../redux/store";
import { getMyProfile } from "../../../redux/users/usersSlice";
import { feedService, storageService } from "../../../services";
import "./index.less";

const CreatorPublicationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const textRef = useRef<any>();
  const dispatch = useDispatch<AppDispatch>();
  const [feed, setFeed] = useState<IFeed>({ isPrivate: true, type: "photo", text: "" });
  const [files, setFiles] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [loadedData, setLoadedData] = useState<number>(0);
  const [totalSize, setTotalSize] = useState<number>(0);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [quickFiles, setQuickFiles] = useState<any>({ id: "", type: "" });

  useEffect(() => {
    if (totalSize > 0) {
      const uploadProgress = Math.round((loadedData / totalSize) * 100);
      const globalProgress = Math.round(uploadProgress);
      setProgress((prev) => {
        if (globalProgress > prev) {
          return globalProgress;
        }
        return prev;
      });
    }
  }, [loadedData]);

  const changedItem = (field: keyof IFeed, value: any) => {
    setFeed((prevFeed) => ({
      ...prevFeed,
      [field]: value,
    }));
  };

  const handleFilesChange = (fileList: UploadFile<[]>[]) => {
    setFiles(fileList);
  };

  const createFeed = async () => {
    if (files.length === 0 && !quickFiles.id) {
      return;
    }
    if (!quickFiles.id && !quickFiles.type) {
      setLoading(true);
      const feedData = new FormData();
      const file = files[0];

      if (file?.originFileObj?.type?.includes("image/")) {
        feedData.append(`files`, file.originFileObj);
      } else {
        setTotalSize(file.size);
        const result = await storageService.uploadFileMultiPart(file, setLoadedData);

        const videoFiles = [
          {
            Location: result.Location,
            Key: result.Key,
            ETag: result.ETag,
            Bucket: result.Bucket,
            filename: file.name,
            size: file.size,
          },
        ];
        feedData.append("videoFiles", JSON.stringify(videoFiles));
      }

      // Set the type of the feed based on the file type (photo or video)
      feed.type = files[0].type.includes("video") ? "video" : "photo";
      for (const [key, value] of Object.entries(feed)) {
        feedData.set(key, value);
      }

      const resp = feedService.createFeed(feedData, setProgress);
      resp
        .then((data: any) => {
          if (data.message) {
            message.error(data.message);
          } else {
            message.success(t("feed-created-successfully"));
            message.info(t("big-file-info"));
            setLoading(false);
            dispatch(addFeed(data));
            dispatch(getMyProfile());
            navigate("/creator/profile/newPost");
          }
        })
        .catch((error) => {
          setLoading(false);
          message.error(error);
        });
    } else {
      setLoading(true);
      const feedData = new FormData();
      feedData.append(`quickFiles`, quickFiles?.id);
      feed.type = quickFiles?.type;
      for (const [key, value] of Object.entries(feed)) {
        feedData.set(key, value);
      }

      const resp = feedService.createFeed(feedData, setProgress);
      resp
        .then((data: any) => {
          if (data.message) {
            message.error(data.message);
          } else {
            message.success(t("feed-created-successfully"));
            message.info(t("big-file-info"));
            setFiles([]);
            setQuickFiles({ id: "", type: "" });
            setLoading(false);
            dispatch(addFeed(data));
            navigate("/creator/profile/newPost");
          }
        })
        .catch((error) => {
          setLoading(false);
          message.error(error);
        });
    }
  };

  return (
    <Spin
      spinning={loading}
      style={{ maxHeight: "100%" }}
      indicator={<SpinnerComponent progress={true} progressValue={progress} message={t("stay-on-page-during-uploading") as string} />}
    >
      <Layout>
        <Layout.Content className="content creatorPublicationContentWrapper">
          <Row className="relative justify-content-center header-title-container">
            <ArrowLeftIconComponent top={10} left={0} navigateTo="/creator/profile" />
            <Typography.Text className="header-title-black-32">{t("publications")}</Typography.Text>
          </Row>
          <Row className="w-full mt-20 justify-content-center">
            <AddMediaComponent
              showTitle={false}
              maxCount={1}
              crop={true}
              onFilesChange={handleFilesChange}
              greenCircle={true}
              iconSize={80}
              addPublication={true}
              setQuickFiles={setQuickFiles}
            />
          </Row>
          <Row
            className="mt-20"
            onClick={() => {
              setIsEdit(true);
            }}
          >
            <Typography.Text className={`feed-description-text ${feed.text ? "white" : ""}`}>
              {feed?.text ? feed.text : t("write-caption-tag-creators-text")}
            </Typography.Text>
          </Row>
          <Divider className="publication-divider mt-50" />
          <Row className="justify-content-center mt-30">
            <Row className="w-full justify-content-between blur-media-main-row">
              <Row className="gap-10 items-start">
                <img src={LockIcon} width={16} alt="lock icon" />
                <div className="blur-media-title-col">
                  <Typography.Text className="blur-media-text">{t("want-blur-media")}</Typography.Text>
                  <Typography.Text className="font-12-regular text-grey-color">{t("want-blur-media-desc")}</Typography.Text>
                </div>
              </Row>
              <Switch className="blur-media-switch" defaultChecked={feed.isPrivate} onChange={(value) => changedItem("isPrivate", value)} />
            </Row>
            <StickyButtonComponent
              title={t("publish")}
              handleClickButton={() => {
                !loading && createFeed();
              }}
              disabled={files.length === 0}
            />
          </Row>
        </Layout.Content>

        <UserIdentificationPublicationModalComponent
          isOpen={isEdit}
          onSave={(text) => {
            setFeed((prevFeed) => ({ ...prevFeed, text }));
            setIsEdit(false);
          }}
        />
      </Layout>
    </Spin>
  );
};

export default CreatorPublicationPage;
